import BodyText from "../../BodyText";
import Heading from "../../Heading";
import React from "react";
import styled from "styled-components";
import ScholasticLogoEn from "../../../images/SecondaryLogoInvoice_en.svg";
import ScholasticLogoCa from "../../../images/SecondaryLogoInvoice_ca.svg";
import { InvoiceInfo } from "./RegisterInvoice";
import { convertYMDToMDY } from "../../../utils/DateConversion";

interface Props {
	language: string;
	number: string;
	children: React.ReactNode;
	FirstPage?: boolean;
	LastPage?: boolean;
	invoiceData?: InvoiceInfo
}

/**
 * Page container for the invoice
 */
export const InvoicePrintablePage = ({
	language,
	number,
	children,
	FirstPage,
	LastPage,
	invoiceData
}: Props) => {
	const invoiceDateFormatted = convertYMDToMDY(invoiceData?.invoiceDate);
	const dueDateFormatted = convertYMDToMDY(invoiceData?.dueDate)

	return (
		<InvoicePrintablePageContainer>
			<PageHeader>
				<PageHeaderLeft>
					<StyledScholasticLogo src={language === "en" ? ScholasticLogoEn : ScholasticLogoCa} />
				</PageHeaderLeft>
				<HeaderText>{language === "en" ? "Invoice" : "Facture"}</HeaderText>
				<PageHeaderRow>
					<AccountName>
						<b>{language === "en" ? "School Name:" : "Nom de l’école :"}</b>{" "}<br/>
						{invoiceData?.schoolName}
					</AccountName>
					<AccountNumber>
						<b>{language === "en" ? "Account #:" : "Nᵒ de compte :"}</b>{" "}<br/>
						{invoiceData?.bookfairAccountId}
					</AccountNumber>
					<InvoiceNumber>
						<b>{language === "en" ? "GST #:" : "Nᵒ de TPS :"}</b>{" "}<br/>
						104745229
					</InvoiceNumber>
				</PageHeaderRow>
			</PageHeader>
			<InvoiceInfoDiv>
				<Group>
					<StyledBodyText>
						<b>{language === "en" ? "Host Contact Information:" : "Coordonnées du/de la responsable :"}</b>
					</StyledBodyText>
					<StyledBodyText>
						{invoiceData?.hostContactInformation.name}
					</StyledBodyText>
					<StyledBodyText>
						{invoiceData?.hostContactInformation.email}
					</StyledBodyText>
				</Group>
				<FairIdText>
						<b>{language === "en" ? "Invoice #/Fair ID:" : "Nᵒ de facture/Nᵒ du Festival :"}</b>{" "}<br/>
						{invoiceData?.fairId}
				</FairIdText>
				<InvoiceDate>
					<StyledBodyText>
						<b>{language === "en" ? "Invoice Date:" : "Date de la facture :"}</b>
					</StyledBodyText>
					<StyledBodyText>
						{invoiceDateFormatted}
					</StyledBodyText>
				</InvoiceDate>
			</InvoiceInfoDiv>
			{children}
			<PageNumber>Page {number}</PageNumber>
			{LastPage && (
				<PayableToScholastic>
					<PayableHeader>
						{language === "en" ? "Make Cheque Payable to Scholastic Book Fairs Canada Inc"
							: "Libellez votre chèque à l’ordre de Scholastic Book Fairs Canada Inc."}
					</PayableHeader>
					<OuterFlexContainer>
						<InnerFlexContainer>
							<PayableText>Scholastic Canada Ltd.,</PayableText>
							<PayableText>175 Hillmount Road,</PayableText>
							<PayableText>Markham, Ontario L6C 1Z7</PayableText>
						</InnerFlexContainer>
						<InnerFlexContainer>
							<StyledBodyText>
								<b>{language === "en" ? "Due Date:" : "Date d’échéance : "}</b>{" "}
								{dueDateFormatted}
							</StyledBodyText>
						</InnerFlexContainer>
					</OuterFlexContainer>
				</PayableToScholastic>
			)}
		</InvoicePrintablePageContainer>
	);
};

const InvoicePrintablePageContainer = styled.div`
    margin: 0.25in;
	position: relative;

    @media print {
		height: 10in;
        margin: 0;
        padding: 0.25in 0.5in;
        overflow: hidden;
        break-after: page;
        break-inside: avoid;
        display: block;
        box-sizing: border-box;
		margin-bottom: 0.25in;
    }
`;

const PageNumber = styled(BodyText)`
	font-size: 10px;
	font-weight: 300;
	line-height: 15px;
	letter-spacing: 0px;
	position: absolute;
	bottom: 0;
	right: 0.5in;
`;
const PageHeaderRow = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
`;

const InvoiceInfoDiv = styled(PageHeaderRow)`
	padding-top: 8px;
	padding-bottom: 12px;
`;

const StyledBodyText = styled(BodyText)`
	color: var(--text-primary, #333);
	font-size: 11px;
	font-style: normal;
	font-weight: 300;
	line-height: 15px;
	text-align: left;
`;
const AccountName = styled(StyledBodyText)`
	width: 220px;
`;
const AccountNumber = styled(StyledBodyText)`
	width: 150px;
`;
const InvoiceNumber = styled(StyledBodyText)`
	width: 115px;
	padding-right: 23px;
`;
const FairIdText = styled(StyledBodyText)`
	width: 150px;
`;
const Group = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	width: 220px;
`;
const InvoiceDate = styled(Group)`
	width: 115px;
	padding-right: 23px;
`;
const PageHeader = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 20px;
`;

const PageHeaderLeft = styled.div`
	display: flex;
	flex-direction: row;
	gap: 13.28px;
	margin-bottom: 11px;
`;
const HeaderText = styled(Heading)`
	color: var(--text-primary, #333);
	font-size: 21px;
	font-style: normal;
	font-weight: 400;
	line-height: 24px;
	padding-bottom: 10px;
	text-align: left;
	width: 100%;
	border-bottom: 3px solid #666666;
	margin-bottom: 8px;
`;
const StyledScholasticLogo = styled.img`
	height: 18px;
`;

const PayableToScholastic = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	border-top: 2px solid #666666;
	padding-top: 10px;
`;

const OuterFlexContainer = styled.div`
	display: flex;
	flex-direction: row;
	gap: 130px;
`;

const InnerFlexContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
`;

const PayableHeader = styled(Heading)`
	color: var(--text-primary, #333);
	font-size: 21px;
	font-style: normal;
	font-weight: 500;
	line-height: 24px;
	margin-bottom: 20px;
	padding-bottom: 0px;
`;
const PayableText = styled(BodyText)`
	color: var(--text-primary, #333);
	font-size: 11px;
	font-style: normal;
	font-weight: 600;
	line-height: 15px;
	margin-bottom: 12px;
`;
